import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { getGameRankings } from '../../api/ranking';
import toast from 'react-hot-toast';
import { useLoader } from '../../context/LoaderContext';

const Rankings = () => {
    const { showLoader, hideLoader } = useLoader();
    const [rankings, setRankings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchRankings = async () => {
        showLoader();
        try {
            const res = await getGameRankings();
            if (res.data && Array.isArray(res.data)) {
                setRankings(res.data);
            } else if (Array.isArray(res)) {
                setRankings(res);
            } else {
                setRankings([]);
            }
        } catch (error) {
            console.error("Ranking fetch error:", error);
            toast.error("Sıralama verileri yüklenemedi.");
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchRankings();
    }, []);

    // Filtered rankings based on search term
    const getFilteredRankings = () => {
        if (!searchTerm) return rankings;

        return rankings.map(game => ({
            ...game,
            ranking: game.ranking.filter(user =>
                user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.userId?.toString().includes(searchTerm)
            )
        })).filter(game => game.ranking.length > 0);
    };

    const displayRankings = getFilteredRankings();

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-3">
                <h6 className="fw-semibold mb-0 text-xl">Puan Sıralaması</h6>
                <div className="navbar-search w-auto">
                    <div className="input-group radius-8 border">
                        <span className="input-group-text bg-base border-0 text-secondary-light">
                            <Icon icon="ion:search-outline" />
                        </span>
                        <input
                            type="text"
                            className="form-control bg-base border-0"
                            placeholder="Kullanıcı ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {displayRankings.length > 0 ? (
                    displayRankings.map((game) => (
                        <div className="col-xxl-4 col-xl-6" key={game.gameId}>
                            <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                                    <h6 className="text-lg fw-bold mb-0 text-primary-600 d-flex align-items-center gap-2">
                                        <Icon icon="heroicons:trophy" className="text-xl text-warning-500" />
                                        {game.gameName}
                                    </h6>
                                    <span className="badge bg-neutral-100 text-secondary-light px-12 py-6 radius-4">
                                        Toplam: {game.ranking.length}
                                    </span>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <table className="table table-hover mb-0">
                                            <thead className="bg-neutral-50 sticky-top">
                                                <tr>
                                                    <th scope="col" className="ps-24 text-uppercase text-xs fw-semibold text-secondary-light">Sıra</th>
                                                    <th scope="col" className="text-uppercase text-xs fw-semibold text-secondary-light">Kullanıcı</th>
                                                    <th scope="col" className="text-end pe-24 text-uppercase text-xs fw-semibold text-secondary-light">Puan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {game.ranking.length > 0 ? (
                                                    game.ranking.map((user, index) => {
                                                        // Top 3 distinct styles
                                                        let rankBadge;
                                                        if (user.rank === 1) rankBadge = '🥇';
                                                        else if (user.rank === 2) rankBadge = '🥈';
                                                        else if (user.rank === 3) rankBadge = '🥉';
                                                        else rankBadge = <span className="w-24-px h-24-px d-flex align-items-center justify-content-center bg-neutral-100 rounded-circle text-xs fw-bold text-secondary-light">{user.rank}</span>;

                                                        return (
                                                            <tr key={user.userId}>
                                                                <td className="ps-24 py-16">
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="text-xl me-2">{rankBadge}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-16">
                                                                    <div className="d-flex flex-column">
                                                                        <span className="fw-medium text-secondary-dark text-sm">
                                                                            {user.userName}
                                                                        </span>
                                                                        <span className="text-xs text-secondary-light opacity-75">ID: {user.userId}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="text-end pe-24 py-16">
                                                                    <span className="badge bg-primary-50 text-primary-600 px-10 py-4 radius-4 fw-bold">
                                                                        {user.totalScore.toLocaleString('tr-TR')}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="text-center py-32">
                                                            <div className="d-flex flex-column align-items-center">
                                                                <Icon icon="heroicons:face-frown" className="text-4xl text-neutral-300 mb-2" />
                                                                <p className="text-secondary-light mb-0 text-sm">Henüz sıralama verisi yok.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-center py-48 bg-base radius-12 shadow-sm border">
                        <Icon icon="heroicons:magnifying-glass" className="text-6xl text-neutral-300 mb-16" />
                        <h5 className="text-secondary-light">Sonuç Bulunamadı</h5>
                        <p className="text-secondary-light opacity-50">Aradığınız kriterlere uygun sıralama verisi bulunmamaktadır.</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default Rankings;
